import React, { useEffect, useCallback, useContext, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { ChildrenPropType } from '../../../prop-types/children';
import { SIDE_PANEL_CONTENT } from './constants';
import { useCurrentUserId } from '../../../hooks/use-current-user-id';
import {
  useDeprecatedLocalStorage,
  generateUserSpecificPersistentKey,
  USER_ORDER_DETAILS_SIDE_PANEL,
} from '../../../hooks/use-local-storage';
import { OrderDetailsRoute } from '../routing';
import {
  ORDER_DETAILS_ENHANCED_RETURN_PATH_SEGMENT,
  ORDER_DETAILS_RETURN_PATH_SEGMENT,
  ORDER_DETAILS_MODAL_FULFILLMENT_PLAN_PATH_SEGMENT,
} from '../../../routes/constants';

const SidePanelContext = React.createContext({});

export const useSidePanelContext = () => useContext(SidePanelContext);

const SidePanelContextProvider = ({ children }) => {
  const userId = useCurrentUserId();
  const [sidePanelContent, setSidePanelContent] = useDeprecatedLocalStorage(
    generateUserSpecificPersistentKey(USER_ORDER_DETAILS_SIDE_PANEL, userId),
    {
      name: SIDE_PANEL_CONTENT.NONE,
    },
  );

  const match = useRouteMatch(OrderDetailsRoute);
  const entityTypePathSegment = match?.params?.entityType;
  const isEnhancedReturn = entityTypePathSegment === ORDER_DETAILS_ENHANCED_RETURN_PATH_SEGMENT;
  const isReturn = entityTypePathSegment === ORDER_DETAILS_RETURN_PATH_SEGMENT;
  const isFulfillmentPlan = entityTypePathSegment === ORDER_DETAILS_MODAL_FULFILLMENT_PLAN_PATH_SEGMENT;

  useEffect(() => {
    setSidePanelContent((previousSidePanelContent) => {
      if (previousSidePanelContent.name === SIDE_PANEL_CONTENT.NONE || previousSidePanelContent.name === SIDE_PANEL_CONTENT.ORDER_SUMMARY) {
        return previousSidePanelContent;
      }
      if (isEnhancedReturn || isReturn) {
        return { name: SIDE_PANEL_CONTENT.RETURNS };
      }
      if (isFulfillmentPlan) {
        return { name: SIDE_PANEL_CONTENT.SHIPMENTS };
      }
      return previousSidePanelContent;
    });
  }, [isEnhancedReturn, isFulfillmentPlan, isReturn, setSidePanelContent]);

  // Unused. Maybe intentional decision to not reset on close.
  // Otherwise why the localStorage?
  const closeSidePanel = useCallback(() => {
    setSidePanelContent(() => {
      return { name: SIDE_PANEL_CONTENT.NONE };
    });
  }, [setSidePanelContent]);

  const toggleSidePanelWithContent = useCallback(
    (contentName) => {
      setSidePanelContent((previousSidePanelContent) => {
        if (previousSidePanelContent.name !== contentName) {
          return { name: contentName };
        }

        return { name: SIDE_PANEL_CONTENT.NONE };
      });
    },
    [setSidePanelContent],
  );

  const value = useMemo(
    () => ({
      sidePanelContent,
      toggleSidePanelWithContent,
      closeSidePanel,
    }),
    [sidePanelContent, toggleSidePanelWithContent, closeSidePanel],
  );

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};

SidePanelContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
};

export default SidePanelContextProvider;
